import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { Spin } from "antd";
import MySegmented from "../../components/segmented/Segmented";
import RadialBarChart from "../../components/chart/RadialBarChar";
import { DeleteServiceApi, GetFileListApi } from "../../api/request/service";
import CreateFileModal from "./components/CreateFileModal";
import Delete from "../../assets/Delete";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import toast from "react-hot-toast";
import ViewDocModal from "./components/ViewDocModal";
import { setIsBookKeeping } from "../../store/Slices/userSlice";
import { useDispatch } from "react-redux";
import CreateFiles from "./components/CreateFiles";

const Files = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const [segmentedValue, setSegmentedValue] = useState(
    state?.status || "unsubmitted"
  );
  const [isLoading, setIsLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [fileCount, setFileCount] = useState({});
  const [isCreateFileOpen, setIsCreateFileOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isDocOpen, setIsDocOpen] = useState(false);
  const [selectedFileForAckDocs, setSelectedFileForAckDocs] = useState(null);

  const primaryColor = getComputedStyle(document?.documentElement)
    .getPropertyValue("--primary")
    ?.trim();

  const fileStatus = [
    {
      label: `Unsubmitted (${fileCount?.unsubmitted || 0})`,
      value: "unsubmitted",
    },
    {
      label: `In-Process Files (${fileCount?.inprocess || 0})`,
      value: "inprocess",
    },
    { label: `Closed Files (${fileCount?.closed || 0})`, value: "closed" },
  ];

  const getFileList = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetFileListApi({ type: segmentedValue });
      if (data?.status) {
        setFileList(data?.data);
        setFileCount(data?.status_counts);
      }
    } catch (error) {
      setFileList([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteFile = async () => {
    if (!selectedFile) return;
    try {
      setIsDeleteLoading(true);
      const { data } = await DeleteServiceApi({ file_id: selectedFile });
      if (data?.status) {
        toast.success(data?.message);
        dispatch(
          data?.data?.is_bookkeeping
            ? setIsBookKeeping(true)
            : setIsBookKeeping(false)
        );
        setIsDeleteModalOpen(false);
        setSelectedFile(null);
        getFileList();
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const handleNavigate = (data) => {
    if (segmentedValue === "unsubmitted") {
      navigate(`/file/${data?.id}`);
    } else {
      if (data?.is_bookkeeping) {
        navigate(`/book_keeping/${data?.id}`, { state: { data: data } });
      } else {
        navigate(`/file/${data?.id}`);
      }
    }
  };

  useEffect(() => {
    getFileList();
  }, [segmentedValue]);

  return (
    <div>
      <CreateFiles type="files" />

      <div className="mt-8 p-5 bg-white border rounded-xl">
        <h2 className="text-xl font-medium mb-5">Files Overview</h2>

        <div className="overflow-x-auto">
          <MySegmented
            options={fileStatus}
            value={segmentedValue}
            setValue={setSegmentedValue}
            disabled={isLoading}
          />
        </div>

        <div>
          {isLoading ? (
            <div className="flex flex-col items-center gap-2 mt-16">
              <Spin />
              <p className="text-secondaryLight1">Loading...</p>
            </div>
          ) : (
            <div className="my-6 grid md:grid-cols-2 xl:grid-cols-3 gap-6">
              {fileList?.length === 0 ? (
                <div className="col-span-full flex justify-center items-center py-10">
                  <p className="text-gray-500">No files found.</p>
                </div>
              ) : (
                fileList?.map((service, index) => (
                  <div
                    key={index}
                    onClick={() => handleNavigate(service)}
                    className="cursor-pointer bg-white rounded-2xl shadow-b-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-gray-100 group "
                  >
                    <div className="flex flex-col h-full">
                      {/* Header */}
                      <div className="flex justify-between items-center px-6 py-4 rounded-t-2xl relative overflow-hidden bg-[#e7e7e742]">
                        <h2 className="text-lg font-bold text-gray-800 capitalize group-hover:text-primary transition-colors">
                          {service?.service_type?.service_name}
                        </h2>

                        {segmentedValue === "unsubmitted" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedFile(service?.id);
                              setIsDeleteModalOpen(true);
                            }}
                            className="p-1.5 rounded-full hover:bg-red-50 transition-colors"
                            title="Delete file"
                          >
                            <Delete className="size-4 text-red-500" />
                          </button>
                        )}

                        {/* Gradient Border */}
                        <div className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-slate-300 to-transparent"></div>
                      </div>

                      {/* Content */}
                      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 p-6 h-full">
                        {/* Left Info */}
                        <div className="flex flex-col gap-4 text-sm w-full">
                          <div>
                            <p className="text-xs text-gray-500">File No.</p>
                            <p className="font-semibold text-gray-800">
                              #{service?.file_number}
                            </p>
                          </div>

                          <div>
                            <p className="text-xs text-gray-500">Created On</p>
                            <p className="font-semibold text-gray-800">
                              {moment(service?.created_at).format("ll")}
                            </p>
                          </div>

                          {segmentedValue === "closed" && (
                            <div className="flex gap-12">
                              <div>
                                <p className="text-xs text-gray-500">Ack No.</p>
                                <p className="font-semibold text-gray-800">
                                  {service?.acknowledge_ment_no || "--"}
                                </p>
                              </div>

                              <div>
                                <p className="text-xs text-gray-500">
                                  Ack Doc.
                                </p>
                                <p className="font-semibold text-gray-800">
                                  {service?.acknowledge?.length ? (
                                    <span
                                      className="text-primary cursor-pointer underline hover:text-primary/80"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSelectedFileForAckDocs(service);
                                        setIsDocOpen(true);
                                      }}
                                    >
                                      View
                                    </span>
                                  ) : (
                                    "--"
                                  )}
                                </p>
                              </div>
                            </div>
                          )}

                          {segmentedValue === "inprocess" && (
                            <div>
                              <p className="text-xs text-gray-500">
                                Now Processing
                              </p>
                              <p className="font-semibold text-gray-800">
                                {service?.running_task_name}
                              </p>
                            </div>
                          )}

                          {service?.status?.toLowerCase() === "unsubmitted" &&
                            service?.unsubmit_reason && (
                              <div>
                                <p className="text-xs text-gray-500">
                                  Reason for Unsubmission
                                </p>
                                <p
                                  className="font-semibold text-gray-800 line-clamp-1"
                                  title={service?.unsubmit_reason}
                                >
                                  {service?.unsubmit_reason}
                                </p>
                              </div>
                            )}
                        </div>

                        {/* Right Radial Chart */}
                        <div className="flex-shrink-0">
                          <RadialBarChart
                            values={[
                              service?.completion_percentage?.toFixed(0) || 0,
                            ]}
                            labels={["Completed"]}
                            colors={[primaryColor]}
                            value={true}
                            height={120}
                            width={120}
                          />
                        </div>
                      </div>

                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      <CreateFileModal
        isOpen={isCreateFileOpen}
        setIsOpen={setIsCreateFileOpen}
      />

      <ViewDocModal
        isDocOpen={isDocOpen}
        setIsDocOpen={setIsDocOpen}
        selectedFileForAckDocs={selectedFileForAckDocs}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        message={`Are you sure you want to delete this file?`}
        onConfirm={handleDeleteFile}
        isLoading={isDeleteLoading}
        loadingLabel={`Deleting service...`}
      />
    </div>
  );
};

export default Files;
